/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* global DOCUMENT_EXPERIENCE_URL */
import { envUtil } from "dc-core";
import { isScriptUrlOnSameOrigin, getUrlWithQueryParameters } from "dc-preview-utils/src/Utils/UrlUtils";
import {
  LOCAL_LM_SERVICE_WORKER_SCOPE_PREFIX,
  LM_SERVICE_WORKER_SCOPE_PREFIX,
  BYTES_IN_MB,
  FILENAME_TOAST_HIDE_TIMEOUT
} from "constants/constants";
import { liquidModeError } from "constants/enums";

export const getLMServiceWorkerScopePrefix = () => {
  if (isScriptUrlOnSameOrigin()) return LM_SERVICE_WORKER_SCOPE_PREFIX;
  return LOCAL_LM_SERVICE_WORKER_SCOPE_PREFIX;
};

export const getLMServiceWorkerScope = () => {
  try {
    const uuid = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : "";
    return `${getLMServiceWorkerScopePrefix()}/${uuid}`;
  } catch (err) {
    if (process.env.JEST_WORKER_ID === undefined) console.error(err);
  }
};

export const getLMServiceWorkerIframeSrc = (serviceWorkerScope, dxVersion, useMinified) => {
  let queryParams = {};
  const hostEnv = envUtil.getHostEnv();
  if (hostEnv !== "prod") {
    queryParams.dxUrl = `${DOCUMENT_EXPERIENCE_URL}/${dxVersion}/${useMinified ? "minified" : "unminified"}`;
  }
  return getUrlWithQueryParameters(`${serviceWorkerScope}/index.html`, queryParams);
};

export const debounce = (func, timeout = 10000) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};

export const sizeOfLiquidModeResource = resourceContent => resourceContent.length / BYTES_IN_MB;

export const getBroadcastChannel = scope => new BroadcastChannel(scope);

export const getErrorMsg = errorType => {
  switch (errorType) {
    case liquidModeError.ADOBDE_SENSEI_ERROR:
      return "liquidmode.error.sensei";
    case liquidModeError.PREPIPELINE_DISQUALIFICATION:
      return "liquidmode.error.disqualified";
    default:
      return "modernviewer.notificationview.defaultMsg";
  }
};
